import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import Button from '../components/ui/Button';
import FormField from '../components/ui/FormField';
import '../App.css';
import { translateError } from '../utils/errorMessages';

const VerifyEmailPage: React.FC = () => {
  const { t } = useTranslation();
  const { tenant } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [isLoading, setIsLoading] = useState(!!token);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const verifyEmail = useCallback(async (verificationToken: string) => {
    setIsLoading(true);
    setError('');

    try {
      await api.auth.verifyEmail(verificationToken);
      setIsSuccess(true);
    } catch (err: any) {
      const message = translateError(err, t, 'auth.invalidVerificationToken');
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (token) {
      verifyEmail(token);
    }
  }, [token, verifyEmail]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resendEmail.trim()) return;

    setResending(true);
    try {
      await api.auth.resendVerification(resendEmail.trim());
      setResendSuccess(true);
    } catch {
      // API always returns success for security
      setResendSuccess(true);
    } finally {
      setResending(false);
    }
  };

  // No token provided
  if (!token) {
    return (
      <div className="page-content">
        <div className="form-container">
          {tenant.name && (
            <div className="organization-badge">
              {tenant.name}
            </div>
          )}

          <h2>{t('auth.verifyEmailTitle')}</h2>

          <div className="error-message" role="alert">
            {t('auth.invalidVerificationToken')}
          </div>

          <Link to="/login" className="form-link">
            {t('auth.backToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="form-container">
        {tenant.name && (
          <div className="organization-badge">
            {tenant.name}
          </div>
        )}

        <h2>{t('auth.verifyEmailTitle')}</h2>

        {isLoading && (
          <p style={{ textAlign: 'center', color: 'var(--text-secondary, #aaa)' }}>
            {t('auth.verifyingEmail')}
          </p>
        )}

        {isSuccess && (
          <>
            <div className="success-message" role="status">
              {t('auth.emailVerified')}
            </div>
            <Link to="/login">
              <Button fullWidth>{t('auth.signIn')}</Button>
            </Link>
          </>
        )}

        {error && (
          <>
            <div className="error-message" role="alert">
              {error}
            </div>

            {resendSuccess ? (
              <div className="success-message" role="status" style={{ marginTop: '1rem' }}>
                {t('auth.verificationResent')}
              </div>
            ) : (
              <form onSubmit={handleResend} style={{ marginTop: '1rem' }}>
                <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #aaa)', marginBottom: '0.75rem' }}>
                  {t('auth.resendVerificationPrompt')}
                </p>
                <FormField htmlFor="resend-email" label={t('common.email')} required>
                  <input
                    type="email"
                    id="resend-email"
                    placeholder={t('auth.enterEmail')}
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    disabled={resending}
                    required
                  />
                </FormField>
                <Button type="submit" fullWidth loading={resending}>
                  {t('auth.resendVerification')}
                </Button>
              </form>
            )}

            <Link to="/login" className="form-link" style={{ marginTop: '1rem', display: 'block' }}>
              {t('auth.backToLogin')}
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
